// Loose-relationship JOIN-candidate builder (Phase 5), split from
// suggestJoinCandidates.ts for the 200-line cap. Loose FKs (general +
// JSON-path via `sourcePath`) surface as JOIN candidates next to real FKs,
// marked `loose` so the JOIN list never presents them as constraints.
//
// The ON of a loose rel is more than a column equality when it has to be:
//   - `sourcePath` rels compare an engine JSON-extract of the source column;
//   - `sourceFilter` (a polymorphic FK's type guard, e.g. target_type='post')
//     and `targetFilter` (e.g. deleted_at IS NULL) are ANDed on, each
//     qualified by its own side's alias in either join direction — kept in
//     the ON so an outer join keeps its unmatched rows.
// Such rels carry the whole predicate as `fkEdge.onPredicate`, the new
// relation already qualified by `suggestedAlias` — nothing is substituted
// later, so a filter literal that reads like a placeholder (`kind = '${1}'`)
// stays as written. Every declared column (the equality's, a filter's field)
// is written in the catalog spelling of the table it lives on
// (`declaredCatalogSpelling`): a declaration may spell Oracle's `CUSTOMER_ID`
// as `customer_id`, and delimited that names no column. The Monaco `snippet` body is the same predicate with the
// new alias as the `${1}` tab stop (`snippetWithAliasStop`: its places are
// found by rendering with a stand-in the text does not hold, so no filter
// value is taken for one). A rel mirroring a real FK edge is de-duped —
// unless it carries filters, which the real FK's ON would drop.
// Alias/snippet helpers are threaded in via `deps`. PRD:
// Ralph/SemanticResolverReadOnly/PRD.md § Slice: Suggestion — Join Candidates
// (FK-driven).

import type { QualifiedName, SqlEngine } from '../../sql-grammar/model';
import type { DatabaseEngineId } from '../../../core/engines/engineTypes';
import type { LooseRelationship } from '@/types/electron';
import { extractTextSql } from '@/features/workspace/common/utils/jsonFilterSql';
import { buildQualifiedFilterSql, filterColumnLookup, type FilterColumn } from '../../workspace/common/utils/qualifiedFilterSql';
import { foldFor } from '../service/schemaProjectionHelpers';
import type { FkDirection, FkEdge, JoinCandidate, SchemaSnapshot, Table } from '../types';
import { lookupRelationColumns, lookupRelationTable, sameRelation } from '../service/snapshotRelations';
import { escapeSnippetText, snippetWithAliasStop } from './snippetAliasStop';
import { catalogIdentSql, declaredCatalogSpelling } from './sqlIdentifierText';

type LooseRelEdge = {
  readonly fromQN: QualifiedName;
  readonly toQN: QualifiedName;
  // Bare source column (catalog spelling). `sourcePath` rels' real ON source
  // side is the alias-qualified JSON-extract built by `buildLooseOnPredicate`.
  readonly fromColumnRaw: string;
  /** The target column (catalog spelling). */
  readonly toColumn: string;
  readonly sourcePath: string | undefined;
};

// Shared pure helpers owned by suggestJoinCandidates.ts, injected via deps.
export type LooseRelJoinDeps = {
  readonly qualifiedNameMatches: (a: QualifiedName, b: QualifiedName) => boolean;
  readonly deriveShortAlias: (name: string) => string;
  readonly disambiguateAlias: (base: string, used: Set<string>) => string;
  readonly buildJoinSnippet: (
    rightQN: QualifiedName,
    suggestedAlias: string,
    rightColumns: readonly string[],
    leftColumns: readonly string[],
    leftAliasName: string,
  ) => string;
};

// SR-RO `SqlEngine` → renderer `DatabaseEngineId` for `extractTextSql` / filters.
const ENGINE_ID_BY_SQL_ENGINE: Readonly<Record<SqlEngine, DatabaseEngineId>> = {
  mssql: 'mssql',
  postgres: 'pgsql',
  mysql: 'mysql',
  oracle: 'oracle',
  sqlite: 'sqlite',
};

function makeLooseQN(name: string, schema: string | undefined, fold: (v: string) => string): QualifiedName {
  const qn: QualifiedName = { name, raw: name, quoted: false, foldedName: fold(name) };
  if (schema) {
    qn.schema = schema;
    qn.foldedSchema = fold(schema);
  }
  return qn;
}

function looseRelToEdge(engine: SqlEngine, rel: LooseRelationship, snapshot: SchemaSnapshot): LooseRelEdge {
  const fold = foldFor(engine);
  const fromQN = makeLooseQN(rel.fromTable, rel.fromSchema, fold);
  const toQN = makeLooseQN(rel.toTable, rel.toSchema, fold);
  const namesIn = (relation: QualifiedName) => columnsIn(snapshot, relation)?.map((c) => c.name);
  return {
    fromQN,
    toQN,
    fromColumnRaw: declaredCatalogSpelling(rel.fromColumn, namesIn(fromQN)),
    toColumn: declaredCatalogSpelling(rel.toColumn, namesIn(toQN)),
    sourcePath: rel.sourcePath || undefined,
  };
}

// The snapshot relation's columns as the filter emitter reads them: catalog
// spelling + data type (for date literals).
function columnsIn(snapshot: SchemaSnapshot, relation: QualifiedName): FilterColumn[] | undefined {
  return lookupRelationColumns(snapshot, relation)?.map((c) => ({ name: c.qualified.name, dataType: c.dataType }));
}

function hasFilters(rel: LooseRelationship): boolean {
  return (rel.sourceFilter?.length ?? 0) > 0 || (rel.targetFilter?.length ?? 0) > 0;
}

// The full ON for a rel that needs more than an equality (JSON path and/or
// filters); undefined for a plain unfiltered rel. The source side is the left
// anchor (ltr) or the new relation `newAlias` (rtl), the target side the other.
function buildLooseOnPredicate(
  edge: LooseRelEdge,
  rel: LooseRelationship,
  direction: FkDirection,
  leftAliasName: string,
  newAlias: string,
  engine: SqlEngine,
  snapshot: SchemaSnapshot,
): string | undefined {
  const dbEngine = ENGINE_ID_BY_SQL_ENGINE[engine];
  if (!dbEngine || (!edge.sourcePath && !hasFilters(rel))) return undefined;
  const ltr = direction === 'left-references-right';
  const sourceAlias = ltr ? leftAliasName : newAlias;
  const targetAlias = ltr ? newAlias : leftAliasName;
  const sourceCol = `${sourceAlias}.${catalogIdentSql(edge.fromColumnRaw, engine)}`;
  const targetCol = `${targetAlias}.${catalogIdentSql(edge.toColumn, engine)}`;
  const sourceExpr = edge.sourcePath ? extractTextSql(dbEngine, sourceCol, edge.sourcePath) : sourceCol;
  const equality = ltr ? `${targetCol} = ${sourceExpr}` : `${sourceExpr} = ${targetCol}`;
  // A filter field in its catalog spelling, the exact column only (`filterColumnLookup`).
  const filters = [
    buildQualifiedFilterSql(rel.sourceFilter, sourceAlias, dbEngine, filterColumnLookup(columnsIn(snapshot, edge.fromQN))),
    buildQualifiedFilterSql(rel.targetFilter, targetAlias, dbEngine, filterColumnLookup(columnsIn(snapshot, edge.toQN))),
  ].filter((sql) => sql.length > 0);
  return [equality, ...filters].join(' AND ');
}

// Byte-for-byte the shared `buildJoinSnippet` wrapper around an ON snippet
// body (`onBody`: escaped, the new alias's places already the `${1}` stop).
function buildPredicateSnippet(rightQN: QualifiedName, suggestedAlias: string, onBody: string): string {
  const schemaPrefix = rightQN.schema ? `${rightQN.schema}.` : '';
  const tableRef = `${schemaPrefix}${rightQN.name}`;
  return `${tableRef} \${1:${suggestedAlias}} ON ${onBody}$0`;
}

function classifyLooseRelDirections(
  leftKey: QualifiedName,
  edge: LooseRelEdge,
  qualifiedNameMatches: LooseRelJoinDeps['qualifiedNameMatches'],
): FkDirection[] {
  const dirs: FkDirection[] = [];
  if (qualifiedNameMatches(leftKey, edge.fromQN)) dirs.push('left-references-right');
  if (qualifiedNameMatches(leftKey, edge.toQN)) dirs.push('right-references-left');
  return dirs;
}

// A loose rel "mirrors" a real FK when it's a single-column, non-JSON,
// UNFILTERED edge with the same folded from.col → to.col as an existing FK. A
// filtered rel never mirrors: the real FK's ON would drop its type guard /
// target scope. NOTE: direction-specific de-dupe — a loose rel that is the
// exact *reverse* of a real FK (to→from) is NOT suppressed and surfaces one
// extra (still valid) candidate. Consistent with `looseFkJoinSnippet.ts`.
function edgeMirrorsExistingFk(fks: readonly FkEdge[], edge: LooseRelEdge, rel: LooseRelationship): boolean {
  if (edge.sourcePath || hasFilters(rel)) return false;
  return fks.some(
    (fk) =>
      fk.fromColumns.length === 1 &&
      fk.toColumns.length === 1 &&
      sameRelation(fk.fromTable, edge.fromQN) &&
      sameRelation(fk.toTable, edge.toQN) &&
      fk.fromColumns[0]?.toLowerCase() === edge.fromColumnRaw.toLowerCase() &&
      fk.toColumns[0]?.toLowerCase() === edge.toColumn.toLowerCase(),
  );
}

// Append loose-FK JOIN candidates to `out`, mutating `usedAliases`.
// `leftRelation` is `leftTable` resolved through the snapshot (schema
// included, the name under the snapshot's fold whatever the query's quoting)
// — declarations are matched against it, so a loose FK declared on
// `mia_s1.orders` never surfaces for `mia_s2.orders`. `leftTable` is what the
// candidates report; `leftAliasName` its qualifier SQL.
export function appendLooseRelJoinCandidates(
  out: JoinCandidate[],
  snapshot: SchemaSnapshot,
  looseRels: readonly LooseRelationship[],
  leftTable: QualifiedName,
  leftRelation: QualifiedName,
  leftAliasName: string,
  usedAliases: Set<string>,
  deps: LooseRelJoinDeps,
): void {
  const engine = snapshot.engineId as SqlEngine;
  for (const rel of looseRels) {
    const relEdge = looseRelToEdge(engine, rel, snapshot);
    if (edgeMirrorsExistingFk(snapshot.foreignKeys, relEdge, rel)) continue;
    for (const direction of classifyLooseRelDirections(leftRelation, relEdge, deps.qualifiedNameMatches)) {
      const ltr = direction === 'left-references-right';
      const rightQN = ltr ? relEdge.toQN : relEdge.fromQN;
      const rightColumn = ltr ? relEdge.toColumn : relEdge.fromColumnRaw;
      const leftColumn = ltr ? relEdge.fromColumnRaw : relEdge.toColumn;

      const rightTable: Table = lookupRelationTable(snapshot, rightQN) ?? { qualified: rightQN, kind: 'table' };

      const suggestedAlias = deps.disambiguateAlias(deps.deriveShortAlias(rightQN.name), usedAliases);
      usedAliases.add(suggestedAlias.toLowerCase());

      // JSON-path / filtered rel → `onPredicate` carries the whole qualified ON
      // so shared formatters emit it verbatim (no alias re-prefix).
      const onFor = (alias: string) =>
        buildLooseOnPredicate(relEdge, rel, direction, leftAliasName, alias, engine, snapshot) ?? '';
      const onPredicate = buildLooseOnPredicate(relEdge, rel, direction, leftAliasName, suggestedAlias, engine, snapshot);
      const snippet =
        onPredicate !== undefined
          ? buildPredicateSnippet(
              rightQN,
              suggestedAlias,
              snippetWithAliasStop(onFor, suggestedAlias) ?? escapeSnippetText(onPredicate),
            )
          : deps.buildJoinSnippet(rightQN, suggestedAlias, [rightColumn], [leftColumn], leftAliasName);

      out.push({
        leftTable,
        rightTable,
        fkEdge: {
          name: rel.id,
          leftColumns: [leftColumn],
          rightColumns: [rightColumn],
          direction,
          ...(onPredicate !== undefined ? { onPredicate } : {}),
        },
        suggestedAlias,
        snippet,
        loose: {
          id: rel.id,
          sourceSide: ltr ? 'left' : 'right',
          ...(rel.sourceFilter?.length ? { sourceFilter: rel.sourceFilter } : {}),
          ...(rel.targetFilter?.length ? { targetFilter: rel.targetFilter } : {}),
        },
      });
    }
  }
}
